import { Package, Plus, Search, X, Layers } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '@/utils/productsAPI';
import { categoriesAPI } from '@/utils/categoriesAPI';
import { logger } from '@/utils/logger';
import type { Product, ProductCategory } from '@/types/models';

interface ProductSelectorProps {
  onSelectProduct: (product: Product) => void;
  onClose: () => void;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  onSelectProduct,
  onClose,
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [searchTerm, selectedCategory]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.categoryId = selectedCategory;

      const response = await productsAPI.getAll(params);
      // ✅ Backend retourne { success: true, data: [...], meta: {...} }
      setProducts(response.data || []);
    } catch (error) {
      logger.error('Erreur chargement produits:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      logger.error('Erreur chargement catégories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    onSelectProduct(product);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-teal-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              {t('products.selectProduct')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex gap-4">
            {/* Barre de recherche */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('products.searchByNameOrDesc')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
            </div>

            {/* Filtre par catégorie */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              disabled={loadingCategories}
            >
              <option value="">
                {loadingCategories ? t('common.loading') : t('products.filters.allCategories')}
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Products List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('products.loadingProducts')}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t('products.noProductsFound')}</p>
              <p className="text-gray-500 text-sm mt-2">
                {t('products.tryDifferentSearch')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className="text-left p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 group-hover:text-teal-600 transition">
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {product.isComposite && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded font-medium">
                            <Layers className="w-3 h-3" />
                            Ouvrage
                          </span>
                        )}
                        {product.ProductCategory && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                            {product.ProductCategory.name}
                          </span>
                        )}
                        {product.TaxRate && (
                          <span className="text-xs text-gray-500">
                            TVA {product.TaxRate.rate}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end">
                      <div className="text-lg font-bold text-teal-600">
                        {Number(product.unitPrice).toFixed(2)}€
                      </div>
                      <div className="mt-2 p-1 bg-teal-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition">
                        <Plus className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {t('products.productCount', { count: products.length })}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              {t('common.actions.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

